import { z } from 'zod';

export const BattleCommandTypeSchema = z.enum([
  'MOVE', 'ATTACK', 'CAPTURE', 'DEFEND', 'HOLD', 'RETREAT', 'DOCTRINE',
]);
export type BattleCommandType = z.infer<typeof BattleCommandTypeSchema>;

export const BattleDoctrinSchema = z.enum(['AGGRESSIVE', 'BALANCED', 'DEFENSIVE']);
export type BattleDoctrine = z.infer<typeof BattleDoctrinSchema>;

export const BattleCommandSchema = z.object({
  battleId: z.string().uuid(),
  fleetId: z.string().uuid(),
  type: BattleCommandTypeSchema,
  targetShipId: z.string().uuid().optional(),
  targetX: z.number().min(0).max(600).optional(),
  targetY: z.number().min(0).max(400).optional(),
  doctrine: BattleDoctrinSchema.optional(),
  idempotencyKey: z.string().min(1).max(128),
});

/** A side's standing order as the battle state reports it (M6/11). */
export const StandingOrderSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('MOVE'), x: z.number(), y: z.number() }),
  z.object({ type: z.literal('ATTACK'), targetShipId: z.string() }),
  z.object({ type: z.literal('HOLD') }),
  z.object({ type: z.literal('DEFEND') }),
  z.object({ type: z.literal('RETREAT'), completesAtTick: z.number().int() }),
]);
export type StandingOrder = z.infer<typeof StandingOrderSchema>;
export type BattleCommandRequest = z.infer<typeof BattleCommandSchema>;

export const SetBattleDoctrineSchema = z.object({
  battleId: z.string().uuid(),
  fleetId: z.string().uuid(),
  doctrine: BattleDoctrinSchema,
});
export type SetBattleDoctrineRequest = z.infer<typeof SetBattleDoctrineSchema>;

export const BattleStateResponseSchema = z.object({
  battleId: z.string().uuid(),
  tick: z.number().int(),
  phase: z.enum(['PREPARING', 'ACTIVE', 'RESOLVING', 'FINISHED']),
  winner: z.enum(['attacker', 'defender', 'draw']).optional(),
  version: z.number().int(),
  attackerFleetId: z.string().uuid(),
  defenderFleetId: z.string().uuid(),
  ships: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    class: z.enum(['corvette', 'frigate', 'cruiser']),
    side: z.enum(['attacker', 'defender']),
    currentHull: z.number().int(),
    maxHull: z.number().int(),
    currentShield: z.number().int(),
    maxShield: z.number().int(),
    x: z.number(),
    y: z.number(),
    destroyed: z.boolean(),
  })),
  tickLog: z.array(z.any()),
  /** Viewer's side, its standing order and doctrine (null order = fighting by doctrine). */
  mySide: z.enum(['attacker', 'defender']).optional(),
  myFleetId: z.string().uuid().optional(),
  myOrder: StandingOrderSchema.nullable().optional(),
  myDoctrine: BattleDoctrinSchema.optional(),
  maxTicks: z.number().int().optional(),
});
export type BattleStateResponse = z.infer<typeof BattleStateResponseSchema>;
